/**
 * `runs` namespace — user-facing strings for the top-level Runs area
 * (`src/runs/`). FLAT camelCase keys, one per line (ADR 0065). Plural keys use
 * i18next `_one`/`_other` suffixes (Intl.PluralRules) with `{{count}}`.
 */
export const messages = {
  // ADR 0755 D3 — the resume token is projected only to an approvals:respond holder.
  noRespondPermission: 'You can see this step, but you don\'t have permission to respond to it.',
  summaryRevision: 'Definition revision',
  summaryRevisionAsRun: 'as-run',
  summaryRevisionHeadMoved: 'workflow edited since',
  summaryRevisionHeadMovedTitle: 'The workflow definition has changed since this run; replay and fork still use the pinned revision shown here.',
  summaryRevisionHeadResolved: 'ran current version',
  summaryRevisionHeadResolvedTitle: 'This run resolved the current definition (its pinned revision was unavailable).',
  // Shared duration formatting (runs/format.ts + provenance fallback)
  durationMs: '{{n}} ms',
  durationSeconds: '{{n}} s',
  durationMinutes: '{{m}}m {{s}}s',

  // useRunAnnotations — review-reason tooltip parts
  reviewReasonFlagged: 'flagged',
  reviewSignalsDegraded: 'Some review signals could not be loaded — the flagged count is a minimum, not a total.',
  qualitySignalsUnavailable: 'Quality signals could not be loaded for this run.',
  runStatsUnavailable: 'Run statistics could not be loaded — the event log did not load, so there is nothing to measure from. This is not the same as the run having produced nothing.',
  runFailedAnnounce: 'This run failed: {{code}}.',
  reviewReasonLowRated: 'low-rated',
  reviewReasonCorrected: 'corrected',

  // CommandCenterPage / Mission Control
  missionEyebrow: 'Mission',
  missionControlTitle: 'Mission Control',
  missionControlLede: 'Live view of every in-flight run for this session. Select a run to watch its agent handoffs and reasoning stream in real time.',
  activeCount_one: '{{count}} active',
  activeCount_other: '{{count}} active',
  needAttentionCount_one: '{{count}} need attention',
  needAttentionCount_other: '{{count}} need attention',
  activeRunsLabel: 'Active runs',
  noActiveRunsTitle: 'No active runs',
  noActiveRunsBody: 'In-flight runs appear here automatically as you start them.',
  newWorkflowRun: 'New workflow run',
  awaitingHumanInput: 'Awaiting human input',
  cancelRunTitle: 'Cancel (kill) this run',
  deleteRunTitle: 'Delete this run permanently',
  deleteRunAria: 'Delete run permanently',
  selectRunTitle: 'Select a run to watch it live',
  selectRunBody: 'Pick a run from the left to stream its agent handoffs and reasoning in real time.',
  forkBranchConfirmTitle: 'Branch from this point? Later steps will run again.',
  forkBranchConfirmBody: 'A branch re-executes every step after this one for real — including payments, emails and other outside effects that already happened. They will happen a second time. To inspect what the run did, use the timeline and the step inspector instead: nothing re-runs there.',
  forkBranchConfirmAction: 'Branch and re-run',
  cancelRunConfirmTitle: 'Cancel this run?',
  cancelRunConfirmBody: 'The run stops now and cannot be resumed. Steps that already ran are not undone.',
  deleteRunConfirm: 'Permanently delete this run? This removes its events and history and cannot be undone.',
  streamConnectionError: 'Event stream connection error (reconnecting)',
  runLabel: 'Run',
  streamingLive: 'Streaming live',
  live: 'Live',
  openFullDetail: 'Open full detail →',
  waitingForAgentActivity: 'Waiting for agent activity',
  noAgentActivityTitle: 'No agent activity',
  waitingForAgentActivityBody: 'This run is live — handoffs and reasoning will stream in as soon as the engine emits them.',
  noAgentActivityBody: 'This run produced no agent handoffs or reasoning events.',

  // RunAgentTrace
  agentActivity: 'Agent activity',
  toolStatusRunning: 'running…',
  toolStatusFailed: 'failed',
  toolStatusOk: 'ok',
  toolInputs: 'inputs',
  toolOutcome: 'outcome',
  decisionFallbackLabel: 'decision',
  decisionLabel: 'Decision: {{label}}',
  decisionRaw: 'raw',

  // RunAnalyticsPanel
  runAnalytics: 'Run analytics',
  statOutcome: 'Outcome',
  outcomeRunning: 'running',
  outcomeCompleted: 'completed',
  outcomeFailed: 'failed',
  outcomeCancelled: 'cancelled',
  statElapsed: 'Elapsed',
  statDuration: 'Duration',
  statNodesDone: 'Nodes done',
  statNodeFailures: 'Node failures',
  statInterruptsRaised: 'Interrupts raised',
  statInterruptsPending: 'Interrupts pending',
  statResumes: 'Resumes',
  qualitySignals: 'Quality signals',
  statAnnotations: 'Annotations',
  statMeanRating: 'Mean rating',
  meanRatingValue: '{{rating}} / 5',
  statCorrections: 'Corrections',
  statFlags: 'Flags',
  mostCorrectedNodes: 'Most-corrected nodes',
  correctionCount_one: '{{count}} correction',
  correctionCount_other: '{{count}} corrections',
  analyticsFootnotePre: "Reliability metrics derive from this run's event log. Quality signals derive from annotations and appear once the host advertises ",
  analyticsFootnotePost: '.',

  // RunAuditPage
  auditLogHeading: 'Audit log —',
  backToRun: 'Back to run',
  auditIntroPre: 'Append-only hash chain + Merkle-rooted signed checkpoints per the ',
  auditIntroMid1: ' profile. Verification re-runs ',
  auditIntroMid2: ' on demand and the export bundles the full ',
  auditIntroMid3: ' for offline re-verification via ',
  auditIntroPost: '.',
  auditProfileMissingPre: 'Host does not advertise ',
  auditProfileMissingPost: '.',
  auditCapsUnknown: 'Couldn\'t check this host\'s audit-integrity support.',
  auditCapsUnknownBody: "The capability read failed, so this host’s support is unknown. Do not reconfigure anything on the strength of it.",
  auditEndpointUnavailablePre: 'The audit endpoint is unavailable. Configure the host to advertise the profile via ',
  auditEndpointUnavailableMid: ' on ',
  auditEndpointUnavailablePost: ' to enable verification.',
  verifying: 'Verifying…',
  reVerify: 'Re-verify',
  downloadCheckpoints: 'Download checkpoints (JSON)',
  hashChainValid: 'Hash chain valid',
  hashChainInvalid: 'Hash chain INVALID',
  auditSeqRange: 'seq {{from}}–{{to}}',
  checkpointCount_one: '{{count}} checkpoint',
  checkpointCount_other: '{{count}} checkpoints',
  checkpointSignaturesValid: 'checkpoint signatures valid',
  checkpointSignaturesInvalid: 'checkpoint signatures INVALID',
  anomalyCount_one: '{{count}} anomaly',
  anomalyCount_other: '{{count}} anomalies',
  anomaliesHeading: 'Anomalies',
  anomalyColAtSeq: 'at seq',
  anomalyColExpected: 'expected prevHash',
  anomalyColActual: 'actual prevHash',
  checkpointTimeline: 'Checkpoint timeline ({{count}})',
  noCheckpointsPre: 'No signed checkpoints in this range. The host emits a checkpoint per its configured cadence (typically every ',
  noCheckpointsPost: ' events); short or in-progress runs may not have one yet.',
  seqPrefix: 'seq {{seq}}',
  checkpointFieldCheckpoint: 'checkpoint:',
  checkpointFieldMerkleRoot: 'merkleRoot:',
  checkpointFieldSignature: 'signature:',
  loadingRunEvents: 'Loading run events…',
  runningInitialVerification: 'Running initial verification…',

  // RunComparePage
  runsEyebrow: 'Runs',
  compareRunsTitle: 'Compare runs',
  runIdAPlaceholder: 'run id A',
  runIdBPlaceholder: 'run id B',
  compare: 'Compare',
  compareLede: 'Side-by-side of two runs (e.g. a run and its fork). Structured semantic diff is future work.',
  noRunSelected: 'No run selected.',
  noRunSelectedBody: 'Paste a run ID above to load it into this column.',
  eventCount_one: '{{count}} event',
  eventCount_other: '{{count}} events',

  // RunConversationPanel
  conversationsAria: 'Multi-turn conversations',
  conversationsHeading: 'Conversations',
  conversationsSubheadPre: 'Multi-turn dialog driven by ',
  conversationsSubheadPost: '.',
  conversationAgentLabel: 'agent',
  conversationCapsLabel: 'caps:',
  conversationClosedBadge: 'closed',
  turnRoleUser: 'user',
  turnRoleAgent: 'agent',
  turnRoleSystem: 'system',
  finalOutcome: 'Final outcome',
  confirmClose: 'Confirm close',
  confirmCloseBodyPre: 'The agent is asking to close this conversation. Confirm to emit ',
  confirmCloseBodyPost: '.',
  closing: 'Closing…',
  resumeInvalidJson: 'Resume value is not valid JSON (the suspend declared an outcomeSchema, so the value MUST be a JSON document).',
  resumeTypeMismatch: 'Resume value top-level type is "{{actual}}"; the outcomeSchema requires {{required}}.',
  agentPromptLabel: 'Agent:',
  resumeJsonPlaceholder: 'JSON matching outcomeSchema…',
  resumeReplyPlaceholder: 'Your reply…',
  sending: 'Sending…',
  sendTurn: 'Send turn',

  // RunCostPanel
  costTotalRow: 'Total',
  costColModel: 'Model',
  costLocalRateTitle: 'Cost from local rate table, not host advisory',
  costColCalls: 'Calls',
  costColIn: 'In',
  costColOut: 'Out',
  costColCost: 'Cost',
  tokensAndCost: 'Tokens & cost',
  costTokensSummary: '{{tokens}} tokens · {{calls}} calls',
  costTableCaption: 'Per-model token and cost rollup',
  costAdvisoryPre: 'Advisory estimates — not billing. ',
  costAdvisoryPost: ' = computed from providers.json rates where the host omitted an estimate.',
  costLocalRateTableTitle: 'Local rate table',
  // ADR 0482 §6 — the per-node cost table.
  costByNodeHeading: 'Cost by node',
  costByNodeCaption: 'Per-node cost attribution',
  costColNode: 'Node',
  costNodeOther: 'Other / unattributed',
  costByNodeStampNote: 'Recorded at run completion (top nodes by spend; the remainder is grouped under "Other").',
  // ux-8 — no promise of a stamp: runs that finished before per-node stamping
  // shipped will never get one, so this stays a description of THIS view.
  costByNodeEventsNote: 'Aggregated from this run\'s usage events — the live view for runs without a recorded per-node stamp.',

  // RunDetailPage
  noRunIdInUrl: 'No run ID in URL.',
  forkedFromPrefix: '↳ Forked from',
  cancelRun: 'Cancel run',
  compareRunTitle: 'Compare this run side-by-side with another',
  compareEllipsis: 'Compare…',
  deleteRunHistoryTitle: 'Permanently delete this run and its history',
  deleteRun: 'Delete run',
  downloadBundleTitle: "Download a JSON bundle of this run's events for support / triage (spec/v1/debug-bundle.md)",
  downloadBundle: 'Download bundle',
  // RunDetailPage — redesigned summary (/polish-screens)
  loadingRun: 'Loading run…',
  summaryStarted: 'Started',
  summaryCompleted: 'Completed',
  summaryCurrentNode: 'Current node',
  summaryParentRun: 'Parent run',
  runErrorFallback: 'Run error',
  nodeErrorHint_email_recipient_suppressed: 'This recipient is on the do-not-send list (bounce, complaint, unsubscribe or a manual hold), so the send was refused before reaching the provider.',
  nodeErrorHint_email_recipient_erased: 'This recipient was erased under a data-subject request, so the send was refused.',
  nodeErrorHint_email_recipient_no_consent: 'This recipient has not consented to marketing email, so the send was refused.',
  nodeErrorHint_email_send_in_flight: 'A send for this message is still in flight elsewhere; retry the node in a moment.',
  nodeErrorHint_email_not_connected: 'No email provider Connection is configured for this workspace, so nothing was sent.',
  nodeErrorHint_email_egress_guard_missing: 'The Email feature is not composed on this host, so marketing sends are refused.',
  nodeErrorHintEmailLink: 'Manage suppressions on the Email page.',
  nodeErrorHintConsentLink: 'Review the erasure, or re-admit the subject, on the Consent page.',
  rawSnapshotSummary: 'Raw snapshot (JSON)',
  eventStream: 'Event stream',
  streamModeTitle: 'SSE stream mode — re-subscribes on change',
  feedbackHeading: 'Feedback',
  feedbackRecorded: 'Recorded: {{label}}. Thanks.',
  rateThisRun: 'Rate this run',
  feedbackGood: 'Good',
  feedbackBad: 'Bad',
  feedbackFlagged: 'flagged',
  flagForReview: 'Flag for review',

  // RunHandoffMap
  handoffDecidedFallback: 'decided',
  handoffSupervisor: 'supervisor: {{decision}}',
  handoffDispatch: 'dispatch {{workflowId}}',
  handoffEscalation: 'escalation: {{kind}}',
  handoffConfidenceFloor: 'conf {{conf}} < floor {{floor}}',
  multiAgentHandoffs: 'Multi-agent handoffs',
  // ADR 0829 — structured handoff note
  handoffNoteShow: 'note',
  handoffNoteTitle: 'Handoff note: {{label}}',
  handoffNoteReceivedTitle: 'Handed off with this note',
  handoffNoteEscalationTitle: 'Why this needs you',
  handoffNoteFromAgent: 'From the agent',
  handoffNoteFromHost: 'Summary from the host',
  handoffNoteFromPerson: 'From a person',
  handoffNoteDecisions: 'Decisions so far',
  handoffNoteDecidedBy: 'decided by {{who}}',
  handoffNoteOpenQuestions: 'Open questions',
  handoffNoteTried: 'Already tried',
  handoffNoteDroppedBecause: 'dropped because {{why}}',
  handoffNoteConfidence: 'Confidence {{value}}',
  handoffNoteConfidenceLevel: 'Confidence: {{level}}',
  handoffNoteConfidenceLow: 'low',
  handoffNoteConfidenceMedium: 'medium',
  handoffNoteConfidenceHigh: 'high',

  // RunMemoryPanel
  memoryColContent: 'Content',
  memoryRedactedTitle: 'Contains host-redacted secret material (SR-1)',
  memoryRedacted: 'redacted',
  memoryWrittenByNode: 'Written by node {{nodeId}} — memory.written',
  memoryWrittenByNodeAgent: 'Written by node {{nodeId}} (agent {{agentId}}) — memory.written',
  memoryColTags: 'Tags',
  memoryColCreated: 'Created',
  memoryExpires: 'Expires {{at}}',
  memoryTtlSuffix: '· TTL',
  memoryLedger: 'Memory ledger',
  memoryEntryCount_one: '{{count}} entry',
  memoryEntryCount_other: '{{count}} entries',
  memoryTenantPrefix: 'What this run wrote to the run history. Agents do not read these entries.',
  memoryHighlightAttributed: 'Each entry names the node that wrote it.',
  memoryHighlightSimple: 'The summary is written when the run completes.',
  memoryTableCaption: 'This run\'s entries',

  // RunOpsPanel
  operations: 'Operations',
  preparing: 'Preparing…',
  downloadDebugBundle: 'Download debug bundle',
  verifyAuditIntegrity: 'Verify audit integrity',
  viewFullAuditLogTitle: 'Full audit-log timeline + checkpoint export',
  viewFullAuditLog: 'View full audit log →',
  auditProfileUnavailablePre: 'Host does not advertise the ',
  auditProfileUnavailablePost: ' profile; verification unavailable.',
  chainValid: 'chain valid',
  chainInvalid: 'chain INVALID',
  opsSeqRange: 'seq {{from}}–{{to}}',
  opsCheckpointCount_one: '{{count}} checkpoint',
  opsCheckpointCount_other: '{{count}} checkpoint(s)',
  opsAnomalyCount_one: '{{count}} anomaly',
  opsAnomalyCount_other: '{{count}} anomaly(ies)',
  opsAnomalySeqPrefix: 'seq {{seq}}: expected',
  opsAnomalyGot: ', got ',
  signedCheckpoints: 'Signed checkpoints',
  opsCheckpointRowPrefix: '@{{seq}} · merkle',

  // RunProvenancePanel
  provAwaitingHuman: 'Awaiting human',
  provHumanResolved_one: 'Human resolved {{count}}',
  provHumanResolved_other: 'Human resolved {{count}}',
  provGateUnresolved_one: '{{count}} gate, unresolved',
  provGateUnresolved_other: '{{count}} gate, unresolved',
  provAutonomous: 'Autonomous — no human gate',
  jsonTruncated: '… (truncated)',
  provenance: 'Provenance',
  provWorkflow: 'Workflow',
  provStarted: 'Started',
  provEngine: 'Engine',
  provCausedBy: 'Caused by',
  provParentRun: 'parent run',
  provCausationIdTitle: 'causation id',
  provModelCalls_one: '{{count}} call(s)',
  provModelCalls_other: '{{count}} call(s)',
  provSubstitutedTitle: 'A requested model class was downgraded (model.capability.substituted)',
  provSubstituted: 'substituted',
  provReasoningSteps_one: '{{count}} reasoning step',
  provReasoningSteps_other: '{{count}} reasoning steps',
  provDecisions_one: '{{count}} decision',
  provDecisions_other: '{{count}} decisions',
  provConfidence: '(confidence {{min}}–{{max}})',
  provToolCalls_one: '{{count}} tool call',
  provToolCalls_other: '{{count}} tool calls',
  provInputs: 'Inputs',
  provOutput: 'Output',
  provCaveat: "Derived from this run's event log — a transparent provenance trail, not a signed attestation record.",

  // RunStepInspector
  stepInspector: 'Step inspector',
  stepInspectorAt: '· at #{{seq}}',
  forkFromHereTitle: 'Fork a new run from this point',
  forkFromHere: 'Fork from here',
  noEventAtSeq: 'No event at #{{seq}}.',
  agentActivityUpToPoint: 'Agent activity up to this point',
  payloadSummary: 'payload',
  compactionNoticeTitle: "Shortened by tool-output compaction.",
  compactionElidedRows_one: '{{n}} row was removed from the middle of a list.',
  compactionElidedRows_other: '{{n}} rows were removed from the middle of a list.',
  compactionEmptiedFields: 'These fields were present but empty and were removed: {{fields}}.',

  // RunTimeline
  segStatusRunning: 'running',
  segStatusCompleted: 'completed',
  segStatusFailed: 'failed',
  segStatusSuspended: 'suspended',
  segStatusCut: 'stopped with the run',
  timelineDurationMs: '{{n}}ms',
  timelineDurationSeconds: '{{n}}s',
  timelineDurationMinutes: '{{m}}m {{s}}s',
  timelineMemWrote_one: 'Wrote {{count}} memory entry',
  timelineMemWrote_other: 'Wrote {{count}} memory entries',
  timelineMemWroteWithRefs_one: 'Wrote {{count}} memory entry → {{refs}}',
  timelineMemWroteWithRefs_other: 'Wrote {{count}} memory entries → {{refs}}',
  timelineRunning: 'running…',
  timelineBarAria: '{{nodeId}}, {{status}}, {{dur}} — select to inspect this step',
  timelineBarTitle: '{{nodeId}} — {{status}} — {{dur}}',
  timelineForkSegmentTitle: "Fork a new run from this segment's last event",
  timelineForkFromSeq: 'fork from #{{seq}}',

  // RunsIndexPage
  exampleWorkflowUppercase: 'openwop-app.uppercase — single-node uppercase',
  exampleWorkflowApprovalGate: 'openwop-app.approval-gate — uppercase gated by an approval interrupt',
  savedWorkflowOption: '{{name}} — {{count}} nodes (saved in builder)',
  runCreatedToast: 'Run created — streaming {{runId}}…',
  savedWorkflowNotRunnable: 'Saved workflow is not runnable: {{message}}',
  signedInAs: 'Signed in as {{name}}',
  anonymousSession: 'Anonymous session (24h lifetime)',
  runColRun: 'Run',
  flaggedForReviewTitle: 'Flagged for review: {{reasons}}',
  reviewChip: 'review',
  runColWorkflow: 'Workflow',
  runColStatus: 'Status',
  runColStarted: 'Started',
  runsTitle: 'Runs',
  runsLede: 'Every workflow execution for this tenant — status, duration, and quality at a glance.',
  // Runs page tabs (Mission Control folded in as "Active runs")
  runsTablistLabel: 'Runs views',
  tabAllRuns: 'All runs',
  tabActiveRuns: 'Active runs',
  createARun: 'Create a run',
  flaggedForReview: 'Flagged for review',
  recentRuns: 'Recent runs',
  filterRuns: 'Filter runs',
  filterAll: 'All',
  flaggedFilterTitle: 'Runs flagged, low-rated, or corrected',
  filterFlagged: 'Flagged',
  filterPlaceholder: 'Filter by run id or workflow…',
  noRunsYetTitle: 'No runs yet',
  noRunsYetBody: 'Submit a workflow and watch it stream — status, events, and outputs land in real time.',
  nothingFlaggedTitle: 'Nothing flagged for review',
  nothingFlaggedBody: 'Thumbs-down, flag, or correct a run to add it to this queue.',
  showAllRuns: 'Show all runs',
  noRunsMatchTitle: 'No runs match this filter',
  noRunsMatchBody: 'Try a different run id or workflow, or clear the status filter.',
  clearFilter: 'Clear filter',
  workflowFieldLabel: 'Workflow',
  inputsFieldLabel: 'Inputs (JSON)',
  inputsModeLabel: 'Input editing mode',
  inputsModeForm: 'Form',
  inputsModeJson: 'JSON',
  inputsJsonHelp: 'Raw JSON for the workflow inputs — the form view edits the same object.',
  inputsEnumNone: '— select —',
  errRequired: 'This field is required.',
  errMin: 'Must be at least {{min}}.',
  errMax: 'Must be at most {{max}}.',
  errStep: 'Must be in steps of {{step}}.',
  errNumber: 'Enter a number.',
  errTooLong: 'Keep this under 5,000 characters.',
  errInteger: 'Enter a whole number.',
  errEnum: 'Choose one of the listed values.',
  errEmail: 'Enter a valid email address.',
  errUri: 'Enter a valid URL.',
  errJson: 'Enter valid JSON.',
  creating: 'Creating…',
  createRun: 'Create run',
  aboutThisApp: 'About this app',
  aboutThisAppBodyPre: "The two seeded workflows are defined in the backend's ",
  aboutThisAppBodyPost: '). The first runs end-to-end without HITL; the second pauses at an approval gate so you can exercise the interrupt-resolution UI.',
  figureCompleted: 'Completed',
  figureFailed: 'Failed',
  figureCancelled: 'Cancelled',
  figureAwaitingInput: 'Awaiting input',
  summaryHeading: 'Summary',
  summaryLastRuns: '· last {{count}} runs',
  runOutcomesAria: 'Run outcomes — select to filter the table below',
  meanDuration: 'Mean duration',
  qualityHeading: 'Quality',
  qualityAnnotated: '· {{annotated}} of {{total}} runs annotated',
  correctionRate: 'Correction rate',
  flagRate: 'Flag rate',
  eventViewAria: 'Event view',
  eventStreamHeading: 'Event stream',
  eventStreamConnError: 'Event stream connection error (will reconnect)',
  runRefreshFailed: 'Couldn’t refresh the run after its last update — the details below may be out of date.',
  streamModeLabel: 'mode',
  eventViewTimeline: 'Timeline',
  eventViewLog: 'Log',
  backToRuns: 'Runs',
  retentionNote: 'This run will be removed on {{date}}. Pin it to keep it.',
  retentionPinned: 'Pinned — this run is kept and will not be removed.',
  retentionPin: 'Pin',
  retentionUnpin: 'Unpin',
  retentionPinTitle: 'Keep this run indefinitely (exempt from retention cleanup)',

  // RFC 0257 (ADR 1027 P3) — a completed run that reached no terminal node.
  statusCompletedNoFinalStep: 'Completed: no final step ran',
  noFinalStepRanNotice: 'This run completed without running any final step: every final step ({{steps}}) was skipped by its conditions, so it produced no deliverable.',

  // ADR 0475 — debug loop: provenance chips + Debug in builder + bulk redrive.
  summaryProvenance: 'Provenance',
  summaryDraftRun: 'Draft run',
  summaryDraftRunTitle: 'This run executed the editing head, not the published revision.',
  summaryDebugRun: 'Debug run',
  summaryDebugRunTitle: 'Execute-from-step debug run (from node {{node}}); upstream nodes used pinned outputs.',
  summaryRedriveOf: 'Redriven',
  summaryRedriveOfTitle: 'This is a fresh run of a failed run — open the original.',
  debugInBuilder: 'Debug in builder',
  debugInBuilderTitle: 'Open this workflow in the builder with this run\'s node outputs pinned.',
  redriveSelected_one: 'Redrive {{count}} run',
  redriveSelected_other: 'Redrive {{count}} runs',
  redriveTitle: 'Start fresh runs of the selected failed runs, on the exact definition each originally ran.',
  loadMoreRuns: 'Load more',
  redriveOutcome: '{{ok}} redriven, {{failed}} failed.',
  redriveRowFailed: '{{runId}}…: {{reason}}.',
  redriveRequestFailed: 'The redrive request failed — nothing was started. Try again in a moment.',
  redriveReasonNotRedrivable: 'not failed or cancelled',
  redriveReasonWorkflowGone: 'its workflow no longer exists',
  redriveReasonRunGone: 'run not found',
  redriveReasonGeneric: 'the server refused it',
  redriveRemaining_one: '{{count}} run is still selected (batches are 25 at a time) — press Redrive again.',
  redriveRemaining_other: '{{count}} runs are still selected (batches are 25 at a time) — press Redrive again.',
  // ── ADR 0554 P3 — compensation obligation timeline + gated recovery ──
  compHeading: 'Compensation obligations',
  compRollup: 'Plan: {{status}}',
  compLoading: 'Loading the compensation plan…',
  compEmptyTitle: 'This run owed no compensation',
  compEmptyBody: 'No node in this workflow declared an inverse action, so nothing was recorded to undo.',
  compForbiddenTitle: 'You do not have access to this run’s compensation plan',
  compForbiddenBody: 'Recovery actions are limited to operators in the run’s own workspace.',
  compUnavailableTitle: 'The compensation plan could not be loaded',
  compUnavailableBody: 'This says nothing about whether work is owed — the read itself failed. Try again in a moment.',
  compScopeUnknown: 'Your permissions could not be read, so recovery actions are hidden. This is not the same as not having them — reload to try again.',
  compChainBroken: 'The audit chain failed verification at entry {{seq}}. Treat this run’s recovery history as untrustworthy and escalate.',
  compState_requested: 'Owed',
  compState_started: 'Undoing',
  compState_completed: 'Undone',
  compState_failed: 'Not undone',
  compState_paused: 'Paused',
  compState_manual_intervention_required: 'Needs a human',
  compUnknownNode: 'Unknown node',
  compAttempts_one: '{{count}} attempt',
  compAttempts_other: '{{count}} attempts',
  compMeta: '{{effectKind}} · {{shape}} · updated {{updatedAt}}',
  compReason: 'Reason: {{reason}}',
  compStartedBy: 'Started by {{actor}}',
  compApprovalPending: 'A waive is waiting on a second approver (approval {{approvalId}}). The effect has NOT been waived yet.',
  compHistoryRow: '{{action}} by {{actor}}, {{from}} → {{to}}, {{at}}',
  compApplied: 'Applied',
  compRecordedNotApplied: 'Recorded, not applied',
  compRetry: 'Retry',
  compStart: 'Start unwind',
  compWaive: 'Waive…',
  compWaiveReasonLabel: 'Why is this effect being left in place?',
  compWaiveHint: 'Required. The reason is recorded in the tamper-evident audit chain with your identity and the obligation’s prior state.',
  compWaiveConfirm: 'Waive this obligation',
  compCancel: 'Cancel',
  compActionApplied: 'Recovery action applied.',
  compActionFailed: 'The recovery action failed.',
  compConflict: 'Someone else acted on this obligation first. The timeline has been reloaded — review it and try again.',
  compApprovalRaised: 'This obligation is approval-gated. A waive request was raised for a second approver.',
  timelineAutonomy_auto: "Auto",
  timelineAutonomy_notify_after: "Notified",
  timelineAutonomy_approved: "Approved",
  timelineAutonomy_blocked: "Blocked",
  timelineAutonomy_rejected: "Rejected",
  timelineAutonomyWithRule: "{{label}} · {{ruleId}}",
  timelineAutonomyRule: "Review policy rule {{ruleId}}",
} as const;
